/**
 * AgentConstellation — bridge wave-2 item 4: the army in formation.
 *
 * PRIOR-ART ATTESTATION: none_exists (new component). Renders ONE SVG <g>
 * (never a standalone <svg>) — mounted inside BridgeCore's children slot
 * (the data-slot="constellation-inner" group, which sits inside the
 * centering translate, so polar()'s local-center-(100,100) coordinates land
 * exactly on the 480 viewBox's heart). Both the r=122 stage ring and the
 * r=150 belt live in this single <g>; the radii differ, the center is shared.
 *
 * STATE DERIVATION — identical semantics to AssemblyLine's dots
 * (AssemblyLine.tsx dotState): sessions are filtered to the active run;
 * open session (completed_at null/undefined) → 'open'; any session, none
 * open → 'completed'; no session / sessions unavailable → 'none'.
 *   open      → fill #FF6600 + ac-glow drop-shadow + pulse at the mood // design-law-allow
 *               period (fixer/second_opinion pulse toward #FF9933, the // design-law-allow
 *               rc-pulse-heal / healing convention).
 *   completed → steady #FF6600 at 70% opacity. // design-law-allow
 *   none      → white at 14% opacity.
 *
 * Labels (DM Mono 8px) render ONLY for open or hovered/focused/pinned nodes
 * — 23 permanent labels is noise; 23 fixed-position nodes is a formation.
 * Every node is a button (role="button" + tabIndex + Enter/Space handler);
 * activation pins/unpins its label. Positions are FIXED — no rotation, no
 * position animation; the only motion is the open-state opacity/fill pulse,
 * bound to the mood period, collapsed by down-mood (JS class strip) and by
 * prefers-reduced-motion (JS class strip AND the CSS media query below —
 * BridgeCore's .bc-root media query covers this subtree too).
 */
import React, { useMemo, useState } from 'react';
import { FleetSession } from '../../services/fleetHudApi';
import { FleetMood, MOOD_STYLE } from '../fleet/fleetMood';
import { ACCENT, polar } from '../fleet/ReactorCore';
import { STAGE_AGENTS } from '../fleet/AssemblyLine';
import { usePrefersReducedMotion } from './BridgeCore';
import {
  BELT_AGENTS,
  OrbitSlot,
  agentSessionState,
  buildConstellation,
  filterRunSessions,
} from './agentOrbit';

/** Amber-shifted pulse for the repair pair — mirrors AssemblyLine's private
    HEALING_AGENT_TYPES (kept local: that const is AssemblyLine's own). */
const HEALING_PULSE_AGENTS: ReadonlySet<string> = new Set(['fixer', 'second_opinion']);
const HEAL_PULSE_COLOR = '#FF9933'; // design-law-allow (healing animation secondary color)

/** The roster→slot mapping is static (fixed formation) — build once. */
const SLOTS: readonly OrbitSlot[] = buildConstellation(STAGE_AGENTS, BELT_AGENTS);

type NodeState = 'open' | 'completed' | 'none';

interface AgentConstellationProps {
  /** Active run's sessions — the SAME prop AssemblyLine receives. undefined
      = unavailable (idle / fetch failed) → every node degrades to 'none'. */
  sessions?: FleetSession[];
  /** Active run id; sessions of other runs never light a node. */
  activeRunId?: string | null;
  /** Fleet mood — binds the open-state pulse period (MOOD_STYLE). */
  mood: FleetMood;
  /** Item 11: node activation (click / Enter / Space) also reports the
      selection upward — BridgeCore lifts it for the BridgeDetail popover. */
  onSelectAgent?: (agentType: string) => void;
  /** Currently selected agent — its node is marked data-selected so the
      FleetHud spotlight fade skips it. */
  selectedAgent?: string | null;
  /** True while any BridgeDetail selection is open. Non-selected nodes strip
      their open-pulse so the CSS 40% fade actually wins (running keyframe
      animations override plain opacity declarations). */
  spotlightActive?: boolean;
  className?: string;
}

/**
 * Component-scoped CSS, ac- prefixed. The pulse keyframes animate opacity
 * (and fill, for the healing pair) — never position. Reduced-motion collapse
 * is three-deep: this media query, BridgeCore's .bc-root media query, and
 * the JS class strip below.
 */
const AC_CSS = `
.ac-node { cursor: pointer; }
.ac-node:focus { outline: none; }
.ac-node:focus-visible .ac-shape { stroke: #FFFFFF; stroke-opacity: 0.9; } /* design-law-allow */
.ac-glow { filter: drop-shadow(0 0 6px rgba(255, 102, 0, 0.9)); }
.ac-node--open { animation-name: ac-pulse; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
.ac-node--heal { animation-name: ac-pulse-heal; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
@keyframes ac-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
@keyframes ac-pulse-heal { 0%, 100% { opacity: 1; fill: ${ACCENT}; } 50% { opacity: 0.75; fill: ${HEAL_PULSE_COLOR}; } }
@media (prefers-reduced-motion: reduce) {
  .ac-root, .ac-root * { animation: none !important; }
}
`;

/** Diamond path (half-diagonal 3) centered on (x, y). */
function diamondPath(x: number, y: number): string {
  return `M ${x.toFixed(2)} ${(y - 3).toFixed(2)} L ${(x + 3).toFixed(2)} ${y.toFixed(2)} L ${x.toFixed(2)} ${(y + 3).toFixed(2)} L ${(x - 3).toFixed(2)} ${y.toFixed(2)} Z`;
}

export default function AgentConstellation({
  sessions,
  activeRunId = null,
  mood,
  onSelectAgent,
  selectedAgent = null,
  spotlightActive = false,
  className,
}: AgentConstellationProps) {
  const reducedMotion = usePrefersReducedMotion();
  // Down mood = zero animation anywhere in the bridge tree (BridgeCore
  // contract); reduced motion strips the classes the same way.
  const motionOff = mood === 'down' || reducedMotion;
  const pulseSeconds = MOOD_STYLE[mood].pulseSeconds;

  // Sessions for THIS run only — the shared selector (agentOrbit), identical
  // to what AgentRosterList uses so rail and constellation never disagree.
  const runSessions = useMemo(
    () => filterRunSessions(sessions, activeRunId),
    [sessions, activeRunId]
  );

  /** AssemblyLine dotState semantics via agentOrbit: open > completed > none. */
  const nodeState = (agentType: string): NodeState => agentSessionState(runSessions, agentType);

  // Hovered/focused node + click-pinned node — the only dim/completed nodes
  // allowed to show a label. (Open nodes always label.)
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);

  return (
    <g className={`ac-root ${className ?? ''}`} data-testid="agent-constellation">
      <style>{AC_CSS}</style>
      {SLOTS.map((slot) => {
        const state = nodeState(slot.agentType);
        const p = polar(slot.orbitRadius, slot.angleDeg);
        const healing = HEALING_PULSE_AGENTS.has(slot.agentType);
        const isSelected = selectedAgent === slot.agentType;
        // Under the item 11 spotlight a non-selected node strips its pulse —
        // the fade (opacity 0.4) must win, and a running keyframe animation
        // would override it. The selected node keeps its mood-bound pulse.
        const animated =
          state === 'open' &&
          !motionOff &&
          pulseSeconds > 0 &&
          (!spotlightActive || isSelected);
        const shapeClass = `ac-shape${state === 'open' ? ' ac-glow' : ''}${
          animated ? (healing ? ' ac-node--heal' : ' ac-node--open') : ''
        }`;
        const shapeStyle: React.CSSProperties = animated
          ? { animationDuration: `${pulseSeconds}s` }
          : {};
        // Hollow diamond = the resting belt shape; state fills it. Circles
        // are always filled — state sets the color/opacity.
        const fill =
          state === 'open'
            ? ACCENT
            : slot.shape === 'diamond'
              ? 'none'
              : state === 'completed'
                ? ACCENT
                : '#FFFFFF'; // design-law-allow
        const fillOpacity = state === 'open' ? 1 : state === 'completed' ? 0.7 : 0.14;
        const stroke =
          slot.shape === 'diamond'
            ? state === 'none'
              ? '#FFFFFF' // design-law-allow
              : ACCENT
            : undefined;
        const strokeOpacity =
          slot.shape === 'diamond' ? (state === 'none' ? 0.14 : state === 'completed' ? 0.7 : 1) : undefined;

        const showLabel = state === 'open' || hovered === slot.agentType || pinned === slot.agentType;
        const labelStart = p.x < 100;
        const togglePin = () =>
          setPinned((cur) => (cur === slot.agentType ? null : slot.agentType));
        // Item 11: activation pins the label AND lifts the selection to
        // BridgeCore (→ BridgeDetail popover + FleetHud spotlight).
        const activate = () => {
          togglePin();
          onSelectAgent?.(slot.agentType);
        };
        // `title` is a valid SVG2 attribute and React renders it on <g> at
        // runtime; React's SVGProps type just doesn't declare it. Cast once
        // here — the acceptance contract requires a real title attribute.
        const titleProp = { title: slot.agentType } as React.SVGProps<SVGGElement>;

        return (
          <g
            key={slot.agentType}
            role="button"
            tabIndex={0}
            {...titleProp}
            aria-label={slot.agentType}
            className="ac-node"
            data-agent-type={slot.agentType}
            data-state={state}
            data-orbit-radius={slot.orbitRadius}
            data-selected={isSelected ? 'true' : undefined}
            onMouseEnter={() => setHovered(slot.agentType)}
            onMouseLeave={() => setHovered((cur) => (cur === slot.agentType ? null : cur))}
            onFocus={() => setHovered(slot.agentType)}
            onBlur={() => setHovered((cur) => (cur === slot.agentType ? null : cur))}
            onClick={activate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            }}
          >
            <title>{slot.agentType}</title>
            {/* Generous invisible hit target — the visible node is 3–3.5px. */}
            <circle cx={p.x} cy={p.y} r={10} fill="transparent" pointerEvents="all" />
            {slot.shape === 'circle' ? (
              <circle
                cx={p.x}
                cy={p.y}
                r={3.5}
                fill={fill}
                fillOpacity={fillOpacity}
                className={shapeClass}
                style={shapeStyle}
              />
            ) : (
              <path
                d={diamondPath(p.x, p.y)}
                fill={fill}
                fillOpacity={fill === 'none' ? undefined : fillOpacity}
                stroke={stroke}
                strokeOpacity={strokeOpacity}
                strokeWidth={1}
                className={shapeClass}
                style={shapeStyle}
              />
            )}
            {showLabel && (
              <text
                x={p.x + (labelStart ? 7 : -7)}
                y={p.y + 3}
                textAnchor={labelStart ? 'start' : 'end'}
                className="font-mono"
                fontSize="8"
                fill="rgba(255,255,255,0.85)"
                pointerEvents="none"
              >
                {slot.agentType}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
